import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ScoreboardFilterBar from './ScoreboardFilterBar';
import ScoreboardTable from './ScoreboardTable';
import ScoreboardDetailModal from './ScoreboardDetailModal';
import LocalText from '../common/LocalText';
import { useToast } from '../../context/ToastContext';
import { useSpaceId } from '../../hooks/useSpaceId';
import { getGames, getScoreboard } from '../../api/gameScoreBoardApi';
import { describeScoreboardRange } from '../../utils/scoreboardRangeLabel';
import { C } from '../../theme/adminTokens';

// How long the search box waits after the last keystroke before triggering a fetch.
const SEARCH_DEBOUNCE_MS = 350;

const DEFAULT_PAGE_SIZE = 25;

/**
 * The real Game Score Board feature: a game selector, a Weekly/Monthly/Custom filter,
 * and a paginated leaderboard table. Visible to any space member (no permission gate —
 * unlike the announcement date above it, this section has no edit-only concept on the
 * backend either).
 *
 * Deliberately independent of the "Game Winner Announcement Date" card in
 * GameScoreBoard.jsx: that date is space-level (one per space, not per game — see
 * LeaderboardController.cs), so game selection here never touches it.
 */
export default function GameScoreboardSection() {
  const spaceId = useSpaceId();
  const { toast } = useToast();

  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [gameId, setGameId] = useState(null);

  // uiFilter drives what the toggle/date-inputs show; appliedQuery drives what was
  // actually fetched. They diverge only while Custom is being edited pre-Apply.
  const [uiFilter, setUiFilter] = useState('monthly');
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  const [appliedQuery, setAppliedQuery] = useState({ filter: 'monthly', from: undefined, to: undefined });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // searchInput is what the text field shows; search is the debounced value actually
  // sent to the API — kept separate so typing doesn't refetch on every keystroke.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [refreshTick, setRefreshTick] = useState(0);

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // What the backend actually resolved the current filter to (e.g. Monthly -> the real
  // first/last day of this calendar month) — the response already carries this, so
  // there's no client-side date math to duplicate/drift out of sync with the server's.
  const [resolvedFrom, setResolvedFrom] = useState(null);
  const [resolvedTo, setResolvedTo] = useState(null);
  // The scoreboard limit a Global Admin set for this game in this space (only the top N players are listed), or null.
  const [maxRows, setMaxRows] = useState(null);
  // Starts true (not false): the games list resolves and this component re-renders
  // before the scoreboard-fetch effect below has a chance to run, so a false-by-default
  // value would flash the empty state ("No scores recorded...") for one frame first.
  const [scoreboardLoading, setScoreboardLoading] = useState(true);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsRow, setDetailsRow] = useState(null);

  // Debounce the search box: only push it into `search` (which drives the fetch effect)
  // after the user pauses typing — a plain setTimeout/clearTimeout, no extra dependency.
  // Skipped entirely while a Custom range is pending Apply: the filter bar tells the user
  // "nothing updates until Apply" in that state, so a search-triggered refetch here (still
  // using the stale, previously-applied filter) would silently contradict that. Whatever
  // was typed gets flushed into `search` when Apply actually runs, see handleApplyCustom.
  useEffect(() => {
    if (uiFilter === 'custom') return undefined;
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, uiFilter]);

  // Load the game catalog once, and default to the first active game.
  useEffect(() => {
    let cancelled = false;
    setGamesLoading(true);
    getGames()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setGames(list);
        const firstActive = list.find((g) => g.isActive) ?? list[0];
        if (firstActive) setGameId(firstActive.id);
      })
      .catch(() => { if (!cancelled) toast.error('Failed to load the games list.'); })
      .finally(() => { if (!cancelled) setGamesLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switching games resets the filter back to Monthly (the agreed default) and clears
  // any in-progress custom range, rather than carrying the previous game's filter over.
  const handleGameChange = (nextGameId) => {
    setGameId(nextGameId);
    setUiFilter('monthly');
    setDraftFrom('');
    setDraftTo('');
    setSearchInput('');
    setSearch('');
    setSortDirection('asc');
    setPage(1);
    setAppliedQuery({ filter: 'monthly', from: undefined, to: undefined });
  };

  const handleSortDirectionChange = (next) => {
    setSortDirection(next);
    setPage(1);
  };

  const handleRefresh = () => setRefreshTick((t) => t + 1);

  const handleDetailsClick = (row) => {
    setDetailsRow(row);
    setDetailsOpen(true);
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setDetailsRow(null);
  };

  const handleFilterChange = (next) => {
    setUiFilter(next);
    if (next === 'custom') return; // wait for Apply — no fetch on bare selection
    setPage(1);
    setAppliedQuery({ filter: next, from: undefined, to: undefined });
  };

  const handleApplyCustom = () => {
    setPage(1);
    // Flush whatever's currently typed — the debounce effect above skips committing
    // `search` while filter === 'custom', so without this an Apply right after typing
    // would fetch with the previous (stale) search term instead of what's on screen.
    setSearch(searchInput);
    setAppliedQuery({ filter: 'custom', from: draftFrom, to: draftTo });
  };

  useEffect(() => {
    if (!spaceId || !gameId) return undefined;
    let cancelled = false;
    setScoreboardLoading(true);
    getScoreboard(gameId, {
      ...appliedQuery, page, pageSize, search, sortDirection,
    })
      .then((data) => {
        if (cancelled) return;
        setRows(data?.items ?? []);
        setTotalCount(data?.totalCount ?? 0);
        setResolvedFrom(data?.fromDate ?? null);
        setResolvedTo(data?.toDate ?? null);
        setMaxRows(data?.maxRows ?? null);
        // The server clamps a page past the end to the last page; follow it so the pager and the rows agree.
        if (data?.pageNumber && data.pageNumber !== page) setPage(data.pageNumber);
      })
      .catch(() => { if (!cancelled) toast.error('Failed to load the scoreboard.'); })
      .finally(() => { if (!cancelled) setScoreboardLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceId, gameId, appliedQuery, page, pageSize, search, sortDirection, refreshTick]);

  return (
    <Box>
      {gamesLoading ? (
        <Typography variant="body2" role="status" sx={{ color: C.muted }}>
          <LocalText>Loading…</LocalText>
        </Typography>
      ) : games.length === 0 ? (
        <Typography variant="body2" role="status" sx={{ color: C.muted }}>
          <LocalText>No games are configured yet.</LocalText>
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <ScoreboardFilterBar
            games={games}
            gameId={gameId}
            onGameChange={handleGameChange}
            filter={uiFilter}
            onFilterChange={handleFilterChange}
            draftFrom={draftFrom}
            draftTo={draftTo}
            onDraftFromChange={setDraftFrom}
            onDraftToChange={setDraftTo}
            onApplyCustom={handleApplyCustom}
            search={searchInput}
            onSearchChange={setSearchInput}
            sortDirection={sortDirection}
            onSortDirectionChange={handleSortDirectionChange}
            onRefresh={handleRefresh}
            disabled={scoreboardLoading}
          />

          {!scoreboardLoading && describeScoreboardRange(appliedQuery.filter, resolvedFrom, resolvedTo) && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 1, bgcolor: '#eff6ff', borderRadius: 2,
            }}
            >
              <EventAvailableIcon sx={{ fontSize: 16, color: C.navy }} />
              <Typography variant="body2" sx={{ fontSize: 12.5, color: C.navy, fontWeight: 600 }}>
                <LocalText>{describeScoreboardRange(appliedQuery.filter, resolvedFrom, resolvedTo)}</LocalText>
              </Typography>
            </Box>
          )}

          {!scoreboardLoading && maxRows != null && (
            <Typography data-testid="scoreboard-limit-note" variant="body2" sx={{ fontSize: 12.5, color: C.muted }}>
              <LocalText>{`Only the top ${maxRows.toLocaleString('en-US')} players are listed.`}</LocalText>
            </Typography>
          )}

          {scoreboardLoading ? (
            <Typography variant="body2" role="status" sx={{ color: C.muted }}>
              <LocalText>Loading…</LocalText>
            </Typography>
          ) : (
            <ScoreboardTable
              rows={rows}
              totalCount={totalCount}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              onDetailsClick={handleDetailsClick}
            />
          )}
        </Box>
      )}

      {detailsOpen && detailsRow && (
        <ScoreboardDetailModal
          open={detailsOpen}
          onClose={handleDetailsClose}
          row={detailsRow}
          gameId={gameId}
          filter={appliedQuery.filter}
          from={appliedQuery.from}
          to={appliedQuery.to}
          rangeFrom={resolvedFrom}
          rangeTo={resolvedTo}
        />
      )}
    </Box>
  );
}
